<script setup>
import { useBraindateLink } from '@/composables/useBraindateLink';
import { getConferenceTimezone, getConferenceTimezoneLabel, isSessionOnDemand } from '~~/shared/utilities/schedule';
import { sponsorPoweredByLine } from '~/composables/helpers/sponsors';
import { useScheduleControls } from '@/composables/useScheduleControls';
import { useScheduleAction } from '@/composables/useScheduleAction';

const dayjs = useDayjs();
// Global feature gates (client-side, time-released) for My Schedule / Save a Seat.
const { showMySchedule, showSeatReservations } = useScheduleControls();

const props = defineProps({
    session: { type: Object, required: true },
    actionRequired: { type: Boolean, default: false },
    isFull: { type: Boolean, default: false },
    hasSavedSeat: { type: Boolean, default: false },
    year: { type: Number, default: 2026 },
});

// "Save a seat" is the only action the orange treatment ever asks for, so it
// can't be action-*required* before seat saving opens. Gated on the same
// time-release as the Save a Seat button itself, otherwise every limited-capacity
// session on My Schedule reads as orange/urgent months early.

// The on-demand glyph. `on_demand` is Sessionboard's checkbox (UNB-753 era
// follow-up); `livestream` is the legacy flag, true on exactly 37 archived 2025
// rows and on nothing in the 2026 catalog. OR-ed rather than replaced so the
// archive keeps the icon it already shows.
const isOnDemand = computed(() => isSessionOnDemand(props.session));

const seatActionRequired = computed(() => props.actionRequired && showSeatReservations.value);

// A clash the attendee has to resolve, set client-side by agenda.vue's
// applyConflicts overlay (the shared feed can't carry it). Deliberately a
// MODIFIER rather than a variant: a conflicting card keeps its own body colour
// and buttons, and only gains the red header/border, the "!" and an appended
// " - ACTION NEEDED" (Jack, 2026-08-20).
const isConflicted = computed(() => props.session.is_conflicted === true);

const variant = computed(() => {
    if (props.session.is_my_event || props.session.is_external) return 'user-event';
    // Full + saved seat → "everything done"
    if (props.hasSavedSeat && props.isFull && props.session.session_reservations === true) return 'full-saved-seat';
    // Full + added + no seat → "you got in but need to grab a seat"
    if (seatActionRequired.value && props.isFull && props.session.session_reservations === true) return 'full-action-required';
    // Limited (not full) + needs action (prop-driven; pre-add nudge)
    if (seatActionRequired.value) return 'action-required';
    // Full session, no user state
    if (props.session.session_reservations === true && props.isFull) return 'full-session';
    if (props.session.session_reservations === true) return 'limited-capacity';
    return 'generic';
});

// See detail-card: the dotted darker-pink vanishes on an orange body.
const onOrangeBody = computed(() => (
    variant.value === 'action-required' || variant.value === 'full-action-required'
));
const hoverUnderlineCls = computed(() => (
    `hover:underline hover:decoration-dotted hover:decoration-[6%] hover:underline-offset-[15.5%] ${
        onOrangeBody.value ? 'hover:decoration-white' : 'hover:decoration-[#7D4566]'}`
));

const cardClasses = computed(() => {
    // Hovering the card itself only raises the purple glow shadow, matching the
    // Figma "Card Drop Shadow" effect. The title owns its hover state —
    // deliberately not a `group`, so hovering the card background doesn't fire
    // every child's hover at once.
    const base = 'relative flex flex-col items-start overflow-clip rounded-[10px] w-full pb-[26px] transition-shadow duration-200 hover:shadow-[0_0_8px_0_rgba(130,12,167,0.5)]';
    const body = {
        'full-session':         'bg-(--pink)',
        'action-required':      'bg-(--orange)',
        'full-action-required': 'bg-(--orange)',
    }[variant.value] || 'bg-white';

    // Replaces the base border rather than adding to it: two border-colour
    // utilities in one class list leaves the winner to CSS source order.
    const border = isConflicted.value
        ? 'border border-[#980000]'
        : (variant.value === 'full-session' ? 'border border-(--dark-pink)' : '');

    return `${base} ${body} ${border}`.trim();
});

const hasHeader = computed(() => variant.value !== 'generic' || isConflicted.value);

// "AI Bug" badge (Figma 3601:47997, 19px) — shown inline after the title on
// schedule-builder–recommended sessions, matching calendar-card. `recommendation`
// (the LLM reason string) is only attached to the viewer's own generated sessions
// on the My Schedule feed, so this is naturally scoped to recommended cards.
//
// The 10px gap and the 2px lift are Nick's, given directly rather than via the
// comp — align-middle alone left the badge sitting low against this card's 21px
// serif title. Calendar-card's badge keeps its own values: its title is smaller,
// so it optically aligns differently and Nick hasn't flagged it.
const isRecommended = computed(() => !!props.session.recommendation);

// UNB-746 (Nick, 2026-08-25): "no great way to adjust this to remove the AI icon
// if it breaks to its own line?" — the badge is appended inline after the title,
// so when the last line of the title fills the width the badge wraps alone onto
// a line of its own and reads as a stray dot.
//
// Removing it on wrap is not something CSS can express: there is no "did this
// wrap" query, and answering it would mean measuring in JS on every resize. The
// ORPHAN is preventable though, which is the actual complaint — tie the badge to
// the title's LAST WORD so the two always wrap together, the standard fix for a
// trailing glyph. The icon is never lost, and it never lands on its own line.
const titleParts = computed(() => {
    const t = String(props.session.title ?? '').trim();
    const i = t.lastIndexOf(' ');
    // A single-word title has no head; the whole thing travels with the badge.
    return i === -1 ? { head: '', tail: t } : { head: t.slice(0, i + 1), tail: t.slice(i + 1) };
});

// The badge inverts on the orange card bodies so it stays legible:
//  • white/light bodies → --dark-pink circle, white sparkle
//  • orange bodies      → white circle, --darker-pink sparkle
const badgeOnOrange = computed(() =>
    variant.value === 'action-required' || variant.value === 'full-action-required'
);
const badgeCircleClass = computed(() => (badgeOnOrange.value ? 'fill-white' : 'fill-(--dark-pink)'));
const badgeSparkleClass = computed(() => (badgeOnOrange.value ? 'fill-[#7d4566]' : 'fill-white'));

const headerClasses = computed(() => {
    const base = 'flex items-center gap-[10px] h-[30px] px-[26px] py-[14px] w-full';
    const bgByVariant = {
        'limited-capacity':     'bg-(--magenta)',
        'action-required':      'bg-(--magenta)',
        'full-session':         'bg-(--dark-pink)',
        'full-saved-seat':      'bg-(--dark-pink)',
        'full-action-required': 'bg-(--dark-pink)',
        'user-event':           'bg-(--purple)',
    };
    // Error red wins over every variant colour — it is the conflict signal.
    if (isConflicted.value) return `${base} bg-[#980000]`;
    // A braindate takes the Braindate pill's own fill (#af264d, the same literal
    // braindate-card.vue uses — it has no token in variables.scss), so the card
    // reads as the same thing the Braindate block advertises. An event the
    // attendee created keeps the purple.
    if (variant.value === 'user-event' && props.session.is_braindate) return `${base} bg-[#af264d]`;
    const bg = bgByVariant[variant.value];
    return bg ? `${base} ${bg}` : '';
});

const baseHeaderLabel = computed(() => {
    switch (variant.value) {
        case 'limited-capacity':
        case 'action-required':   // intentionally reuses LIMITED CAPACITY label
            return 'LIMITED CAPACITY';
        case 'full-session':
        case 'full-saved-seat':
        case 'full-action-required':case 'full-action-required':
            return 'SESSION IS FULL';
        // A braindate is a My Event synced from Bizzabo; it says so rather than
        // claiming the attendee created it.
        case 'user-event':   return props.session.is_braindate ? 'BRAINDATE' : 'MY EVENT';
        default:             return '';
    }
});

// A conflict appends rather than replaces, so the card keeps saying what it
// already said. A variant with no header of its own reads as "ACTION NEEDED".
// Standby appends the same way a conflict does, and for the same reason: the
// card keeps saying what it already said. "SESSION IS FULL" is still true — the
// attendee is on the list precisely BECAUSE it is full — so this adds rather
// than replaces. "Waitlisted", never "Standby" (client, 2026-09-03). This REVERSES the
// earlier ruling — the app said Standby everywhere the attendee could read
// it — so the Figma's "WAITLISTED" is now the one that matches.
const standbyHeaderLabel = computed(() => {
    if (!seatButtonWaitlisted.value) return baseHeaderLabel.value;
    // APPENDED to whatever the header already said, not matched against
    // 'SESSION IS FULL'. Bizzabo answers 202 on its own reading of capacity, and
    // `is_full` is the stalest field we hold — bizzabo:capacity writes it every
    // 30 minutes — so an attendee can hold a standby place on a session our data
    // still calls LIMITED CAPACITY. Keying the suffix to our guess left the
    // header silent in exactly that case, while the button beside it was filled.
    return baseHeaderLabel.value ? `${baseHeaderLabel.value} - WAITLISTED` : 'WAITLISTED';
});

const headerLabel = computed(() => {
    if (!isConflicted.value) return standbyHeaderLabel.value;
    return standbyHeaderLabel.value ? `${standbyHeaderLabel.value} - ACTION NEEDED` : 'ACTION NEEDED';
});

const timeClasses = computed(() => {
    if (variant.value === 'action-required' || variant.value === 'full-action-required') return 'text-white';
    if (variant.value === 'full-session') return 'text-[#7d4566]';
    return 'text-(--dark-pink)';
});

const titleClasses = computed(() => {
    if (variant.value === 'action-required' || variant.value === 'full-action-required') return 'text-white';
    return 'text-(--magenta)';
});

// Sponsor styling currently follows title color — kept separate so the Figma
// design system can diverge here (lighter weight, different color) without
// rewriting the variant logic.
const sponsorClasses = computed(() => {
    if (variant.value === 'action-required' || variant.value === 'full-action-required') return 'text-white';
    return titleClasses.value;
});

const videoIconClasses = computed(() => {
    if (variant.value === 'action-required' || variant.value === 'full-action-required') return 'text-white';
    return 'text-(--magenta)';
});

// A full session cannot offer a seat, only a place in the queue — so the seat
// button becomes a standby affordance there, label and icon together. Limited
// (not full) sessions keep "Save a Seat" and the chair glyph.
//
// Both saved-seat sources are read: the variant machine decides
// 'full-saved-seat' from the PROP while seatButtonSaved derives from the user
// session, and reading only one let the header and the button disagree.
const seatIsStandby = computed(() =>
    seatButtonWaitlisted.value
    || (props.isFull
        && !seatButtonSaved.value
        && !props.hasSavedSeat
        && props.session.session_reservations === true)
);

const hasTwoButtons = computed(() =>
    variant.value === 'limited-capacity'
    || variant.value === 'action-required'
    || variant.value === 'full-saved-seat'
    || variant.value === 'full-action-required'
    // Full sessions used to drop this button — "a session at capacity cannot be
    // reserved, so Save a Seat is a dead action" (Bri via Marker.io, Asana
    // 1217733984444914; Nick's 1217697012897127). That premise no longer holds:
    // Bizzabo answers a PUT on a full session with 202 and puts the attendee on
    // its standby list, verified against the live API 2026-09-02. Joining
    // standby IS an action now, so the button stays and reads "Join Waitlist".
    || variant.value === 'full-session'
);

const actionsRowClasses = computed(() => {
    const base = 'flex flex-wrap gap-[27px] items-start w-full';
    return hasTwoButtons.value ? `${base} justify-between` : `${base} justify-start`;
});

// Per-button styling so a single card can mix saved + unsaved button states
// (e.g. full-action-required: Add is filled magenta, Seat is outlined white).
// kind is 'add' or 'seat'.
//
// Hover treatment differs by card background:
//   - Orange-card variants (action-required, full-action-required) → hover: white bg + magenta icon
//   - All other variants → hover: orange bg + white icon
// Add button state + "Add to My Schedule" flow — shared with the other
// agenda cards and the session drawer via useScheduleAction (see that file
// for the enqueue → conflict→doubleBook → refreshSession → emit →
// seatReminder flow).
// seatButtonSaved / seatButtonWaitlisted drive this card's seat aria-label.
// They were referenced in the template but never destructured here, and an
// undeclared identifier in a <script setup> template silently resolves to
// undefined rather than erroring — so the label read 'Save a Seat' forever,
// on a held seat and on a waitlist place alike, and a reload never fixed it.
const { busy, addButtonSaved, seatButtonSaved, seatButtonWaitlisted, handleAdd, handleSeat } = useScheduleAction(
    toRef(props, 'session'),
    { extraSaved: () => variant.value === 'user-event' },
);

const buttonClasses = (kind) => {
    const base = 'size-[34px] rounded-full flex items-center justify-center shrink-0 border transition-colors duration-200';
    const isOrangeCard = variant.value === 'action-required' || variant.value === 'full-action-required';
    const hover = isOrangeCard
        ? 'hover:bg-white hover:border-white hover:text-(--magenta)'
        : 'hover:bg-(--orange) hover:border-(--orange) hover:text-white';

    // Fill means SAVED, on BOTH buttons. The add button already honoured this;
    // the seat button never did, so a confirmed seat kept an outline here and
    // only its aria-label changed. Same rule as detail-card.vue.
    // A standby place is HELD, exactly as a seat is — pressing the button gives
    // it up — so it takes the filled treatment too. detail-card and
    // compact-session-card have always done this; these two checked
    // seatButtonSaved alone, so someone on the list saw a button identical to an
    // unpressed "Save a Seat" and nothing suggested it could be pressed to leave.
    const saved = kind === 'add'
        ? addButtonSaved.value
        : (seatButtonSaved.value || seatButtonWaitlisted.value);
    if (saved) {
        return `${base} ${hover} bg-(--magenta) border-(--magenta) text-white`;
    }
    if (variant.value === 'full-action-required') {
        // Added but still owed a seat: the unsaved seat button outlines white
        // against the orange card.
        return `${base} ${hover} border-white text-white`;
    }
    if (variant.value === 'action-required') {
        return `${base} ${hover} border-white text-white`;
    }
    if (variant.value === 'user-event') {
        // My Event is implicitly always added (removing it would delete the event)
        // → Add button renders in the filled (saved) state.
        return `${base} ${hover} bg-(--magenta) border-(--magenta) text-white`;
    }
    return `${base} ${hover} border-(--magenta) text-(--magenta)`;
};

const formattedTime = computed(() => {
    if (!props.session.start_date_and_time) return '';
    const start = dayjs(props.session.start_date_and_time).tz(getConferenceTimezone(props.year));
    const end = props.session.end_date_and_time
        ? dayjs(props.session.end_date_and_time).tz(getConferenceTimezone(props.year))
        : null;
    const startStr = start.format('h:mma');
    const endStr = end ? end.format('h:mma') : '';
    const tzLabel = getConferenceTimezoneLabel(props.year);
    return end ? `${startStr} - ${endStr} ${tzLabel}` : `${startStr} ${tzLabel}`;
});

const formattedSponsor = computed(() => sponsorPoweredByLine(props.session));

const sessionLink = computed(() =>
    `/sessions/${props.session.unique_id || props.session.id}`
);

const hasSponsor = computed(() => !!formattedSponsor.value);

// Pass the resolved NuxtLink component (not the bare string 'nuxt-link') to the
// root `:is`. The string form renders a dead `<nuxt-link>` custom element that
// never navigates — so the card/title click never reaches /sessions/{id}, and
// the global modal.global.js middleware never rewrites it into the ?drawer=
// query that opens the session drawer. Same fix as calendar-card.vue.
const NuxtLink = resolveComponent('NuxtLink');

const openMyEvent = inject('openMyEvent', null);
const isMyEvent = computed(() => props.session.is_my_event === true);

// A braindate is a My Event the attendee did not create and cannot change: it
// is synced from Bizzabo, and editing it here would be overwritten on the next
// sync while DELETING it would clear their schedule without cancelling the
// actual booking. So it never opens the editor — it goes to Braindate, which is
// the only place the conversation can actually be managed.
//
// Routed through useBraindateLink for the same reasons the Braindate block uses
// it: it appends the SSO pair so the attendee lands signed in, and it validates
// the destination against the configured Braindate origin. That validation is
// not ceremony here — the url is lifted out of HTML that Braindate writes into
// Bizzabo, which is no more ours to trust than a Builder field.
// Deliberately NOT `&& event_url` — being a braindate is what makes it
// read-only, and whether we have a link to send them to is a separate question.
// Tying the two together meant a synced braindate with no permalink fell
// through to the editor, which is the exact thing this prevents.
const isBraindate = computed(() => props.session.is_braindate === true);
const { href: braindateHref, target: braindateTarget } = useBraindateLink(() => props.session.event_url);
// Only render as a link when there is somewhere to go; a braindate with no
// permalink stays inert rather than becoming a dead anchor.
const braindateLinked = computed(() => isBraindate.value && !!braindateHref.value);

const handleCardClick = (e) => {
    if (!isMyEvent.value) return;            // normal sessions keep their nuxt-link
    // A braindate renders as a real anchor (below), so the browser owns the
    // navigation — cmd-click, middle-click, "open in new tab" and the sr-only
    // announcement all come free, and none of them survive a window.open().
    if (isBraindate.value) return;
    e.preventDefault();
    openMyEvent?.(props.session._event);
};
</script>

<template>
    <component
        :is="braindateLinked ? 'a' : (isMyEvent ? 'div' : NuxtLink)"
        :to="isMyEvent ? undefined : sessionLink"
        :href="braindateLinked ? braindateHref : undefined"
        :target="braindateLinked ? braindateTarget : undefined"
        data-test="overview-card"
        :class="[cardClasses, isMyEvent ? 'cursor-pointer' : '']"
        :role="isMyEvent && !braindateLinked ? 'button' : undefined"
        :tabindex="isMyEvent && !braindateLinked ? 0 : undefined"
        @click="handleCardClick"
        @keydown.enter="handleCardClick"
        @keydown.space="handleCardClick">
        <!-- Header bar (all variants except generic) -->
        <div v-if="hasHeader" :class="headerClasses">
            <!-- Alert icon for action-required & full-action-required -->
            <span
                v-if="variant === 'action-required' || variant === 'full-action-required' || isConflicted"
                class="inline-flex items-center justify-center size-[14px] rounded-full bg-white text-(--magenta) text-[11px] font-bold leading-none shrink-0"
            >!</span>
            <span class="font-hubspot-sans font-medium text-[12px] leading-[1.5] tracking-[0.84px] uppercase text-white">
                {{ headerLabel }}
            </span>
        </div>

        <!-- Body -->
        <div class="flex flex-col gap-[18px] items-start pt-[23px] px-[26px] w-full">
            <!-- Content stack -->
            <div class="flex flex-col gap-[8px] items-start w-full">
                <!-- Time row -->
                <div class="flex items-center w-full gap-[14px]" :class="isOnDemand ? 'justify-between' : ''">
                    <span
                        class="text-[14px] leading-[1.5] tracking-[0.21px] font-hubspot-sans font-normal"
                        :class="timeClasses"
                    >{{ formattedTime }}</span>
                    <!-- Video / livestream icon -->
                    <span
                        v-if="isOnDemand"
                        class="shrink-0 inline-flex"
                        :class="videoIconClasses"
                        aria-hidden="true">
                        <UIcon name="local-on-demand" class="w-[15px] h-[10px]" />
                    </span>
                </div>

                <!-- Title block -->
                <div class="flex flex-col gap-[11px] items-start w-full">
                    <p
                        :class="['font-hubspot-serif font-normal text-[21px] leading-[1.3] tracking-[0.105px]', hoverUnderlineCls, titleClasses]"
                    >{{ titleParts.head }}<!--
                        --><span class="whitespace-nowrap">{{ titleParts.tail }}<!--
                        --><svg
                            v-if="isRecommended"
                            width="19"
                            height="19"
                            viewBox="0 0 19 19"
                            fill="none"
                            role="img"
                            aria-label="AI-recommended session"
                            class="relative -top-[2px] inline-block align-middle ml-[10px] shrink-0"
                        >
                            <circle cx="9.5" cy="9.5" r="9.5" :class="badgeCircleClass" />
                            <g :class="badgeSparkleClass" transform="translate(4.5 4.5)">
                                <path d="M10 6.22861C10 6.31924 9.92648 6.39275 9.83654 6.39275C8.4011 6.39275 6.70228 8.09122 6.70228 9.52637C6.70228 9.617 6.62876 9.6905 6.53882 9.6905C6.44888 9.6905 6.37536 9.617 6.37536 9.52637C6.37465 8.09122 4.67582 6.39275 3.24038 6.39275C3.14973 6.39275 3.07692 6.31924 3.07692 6.22861C3.07692 6.13798 3.15044 6.06447 3.24038 6.06447C4.67582 6.06447 6.37465 4.36599 6.37465 2.93157C6.37465 2.84093 6.44817 2.76743 6.5381 2.76743C6.62804 2.76743 6.70156 2.84093 6.70156 2.93157C6.70156 4.36599 8.40039 6.06447 9.83583 6.06447C9.92648 6.06447 9.99929 6.13798 9.99929 6.22861H10Z" />
                                <path d="M4.61538 2.76719C4.61538 2.82761 4.56637 2.87662 4.50641 2.87662C3.54945 2.87662 2.4169 4.00893 2.4169 4.96569C2.4169 5.02612 2.36789 5.07512 2.30793 5.07512C2.24797 5.07512 2.19896 5.02612 2.19896 4.96569C2.19848 4.00893 1.06593 2.87662 0.108972 2.87662C0.0485379 2.87662 0 2.82761 0 2.76719C0 2.70677 0.0490138 2.65776 0.108972 2.65776C1.06593 2.65776 2.19848 1.52545 2.19848 0.569161C2.19848 0.508739 2.2475 0.459736 2.30745 0.459736C2.36741 0.459736 2.41643 0.508739 2.41643 0.569161C2.41643 1.52545 3.54898 2.65776 4.50594 2.65776C4.56637 2.65776 4.61491 2.70677 4.61491 2.76719H4.61538Z" />
                            </g>
                        </svg></span></p>
                    <p
                        v-if="hasSponsor"
                        class="font-hubspot-sans font-light text-[14px] leading-[1.5] tracking-[0.28px]"
                        :class="sponsorClasses"
                    >{{ formattedSponsor }}</p>
                </div>
            </div>

            <!-- Actions row — buttons gated solely by the global, time-released flags -->
            <div v-if="!isMyEvent && (showMySchedule || (hasTwoButtons && showSeatReservations))" :class="actionsRowClasses">
                <!-- Add to My Schedule -->
                <button
                    v-if="showMySchedule"
                    :class="[buttonClasses('add'), addButtonSaved ? 'group/addbtn' : '']"
                    :aria-label="addButtonSaved ? 'Added to My Schedule' : 'Add to My Schedule'"
                    :disabled="busy"
                    @click.stop.prevent="handleAdd"
                >
                    <!-- When added, hovering rotates the + into an X (remove cue). -->
                    <Plus class="size-[11px] transition-transform duration-200 group-hover/addbtn:rotate-45 motion-reduce:transition-none" />
                </button>

                <!-- Save a Seat (variants with hasTwoButtons) -->
                <button
                    v-if="hasTwoButtons && showSeatReservations"
                    :class="buttonClasses('seat')"
                    :aria-label="seatButtonWaitlisted ? 'Waitlisted' : seatButtonSaved ? 'Seat Saved' : (seatIsStandby ? 'Join Waitlist' : 'Save a Seat')"
                    @click.stop.prevent="handleSeat"
                >
                    <TimeCircle v-if="seatIsStandby" class="size-[14px]" />
                    <Seat v-else class="w-[10.167px] h-[11.611px]" />
                </button>
            </div>
        </div>
        <!-- Same announcement MessLink and the Braindate block append to an
             external link; without it the new tab is signalled only visually.
             No `rel` — browsers imply noopener for target=_blank. -->
        <span v-if="braindateLinked && braindateTarget" class="sr-only">(opens in a new tab)</span>

    </component>
</template>

